// ========================================
// TypeScript Interfaces for Calendar
// ========================================

export interface LocalTime {
  hour: number;
  minute: number;
  second?: number;
  nano?: number;
}

export interface CalendarEvent {
  id: number;
  date: string; // Format: YYYY-MM-DD
  startTime: LocalTime | string | null;
  endTime: LocalTime | string | null;
  status: 'PENDING' | 'APPROVED' | 'BLOCKED';
  teamCreatorId: number;
  teamCreatorName: string;
  sportsClubCreatorName: string;
  teamOpponentId?: number;
  teamOpponentName?: string;
  sportsClubOpponentName?: string;
  hasPitchAvailable?: boolean;
  isOwnTeamConflict?: boolean; // Flaga dla konfliktów z własną drużyną (blokuje 1h45min przed startem)
}

export interface TimeSlot {
  date: string; // Format: YYYY-MM-DD
  hour: number;
}

export interface TimeBlockClickData {
  date: Date;
  hour: number;
  dateStr: string;
}

export interface EventClickData {
  event: CalendarEvent;
  hour: number;
}

export interface DayHeaderClickData {
  date: Date;
  dateStr: string;
  isWeekend: boolean;
}

// ========================================
// Calendar Utility Functions
// ========================================

/**
 * Zwraca poniedziałek dla danego tygodnia
 */
export const getStartOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

/**
 * Generuje tablicę 7 dat dla tygodnia (od poniedziałku do niedzieli)
 */
export const getWeekDays = (weekStart: Date): Date[] => {
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return date;
  });
};

/**
 * Zwraca tablicę godzin do wyświetlenia w kalendarzu (8-23)
 */
export const getHoursForDay = (): number[] => {
  const startHour = 8;
  const endHour = 23;
  return Array.from(
    { length: endHour - startHour + 1 },
    (_, i) => startHour + i
  );
};

/**
 * Zwraca wszystkie unikalne godziny (alias dla getHoursForDay dla kompatybilności)
 */
export const getAllHours = (): number[] => {
  return getHoursForDay();
};

/**
 * Formatuje datę w formacie YYYY-MM-DD (lokalnie, bez konwersji UTC)
 */
export const formatLocalDate = (year: number, month: number, day: number): string => {
  const yyyy = year;
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Parsuje godzinę z obiektu LocalTime lub stringa
 */
export const parseHour = (time: LocalTime | string | null | undefined): number => {
  if (!time) return 0;

  if (typeof time === 'object' && 'hour' in time) {
    return time.hour;
  }

  if (typeof time === 'string') {
    const parts = time.split(':');
    return parseInt(parts[0], 10);
  }

  return 0;
};

/**
 * Parsuje minuty z obiektu LocalTime lub stringa
 */
export const parseMinute = (time: LocalTime | string | null | undefined): number => {
  if (!time) return 0;

  if (typeof time === 'object' && 'minute' in time) {
    return time.minute;
  }

  if (typeof time === 'string') {
    const parts = time.split(':');
    if (parts.length >= 2) {
      return parseInt(parts[1], 10);
    }
  }

  return 0;
};

/**
 * Zwraca klasę CSS quarter-fill na podstawie minuty
 * 0 -> 100%, 15 -> 75%, 30 -> 50%, 45 -> 25%
 */
export const getQuarterFillClass = (minute: number): string => {
  if (minute === 0) return 'quarter-fill-0';
  if (minute === 15) return 'quarter-fill-15';
  if (minute === 30) return 'quarter-fill-30';
  if (minute === 45) return 'quarter-fill-45';
  // Dla innych minut zaokrąglij do najbliższego kwadransa
  if (minute < 8) return 'quarter-fill-0';
  if (minute < 23) return 'quarter-fill-15';
  if (minute < 38) return 'quarter-fill-30';
  return 'quarter-fill-45';
};

/**
 * Filtruje wydarzenia dla konkretnego bloku czasowego (data + godzina)
 */
/**
 * Zwraca wydarzenia "all-day" (bez konkretnej godziny) dla danego dnia
 */
export const getAllDayEvents = (
  events: CalendarEvent[],
  date: Date
): CalendarEvent[] => {
  const dateStr = formatLocalDate(date.getFullYear(), date.getMonth(), date.getDate());

  return events.filter(event => {
    return event.date === dateStr && event.startTime === null && event.endTime === null;
  });
};

export const getEventsForTimeBlock = (
  events: CalendarEvent[],
  date: Date,
  hour: number
): CalendarEvent[] => {
  const dateStr = formatLocalDate(date.getFullYear(), date.getMonth(), date.getDate());

  // Bufor czasowy przed konfliktem własnej drużyny (1h45min = 105 minut)
  // Rozszerzamy zakres dla eventów z isOwnTeamConflict aby uwzględnić blokadę przed startem
  const OWN_TEAM_CONFLICT_BUFFER_HOURS = 2; // Zaokrąglone w górę z 1h45min

  return events.filter(event => {
    if (event.date !== dateStr) return false;

    // ZMIANA: Wydarzenia bez czasu będą renderowane osobno jako all-day events
    // NIE zwracaj ich tutaj
    if (event.startTime === null && event.endTime === null) {
      return false;
    }

    // Sprawdź czy godzina mieści się w zakresie [start, end)
    let eventStartHour = parseHour(event.startTime);
    let eventEndHour = parseHour(event.endTime);
    const eventEndMinute = parseMinute(event.endTime);

    // Dla konfliktów własnej drużyny rozszerz zakres o bufor czasowy przed startem
    if (event.isOwnTeamConflict) {
      eventStartHour = Math.max(0, eventStartHour - OWN_TEAM_CONFLICT_BUFFER_HOURS);
    }

    // Jeśli endTime to 00:00 (północ), traktuj jako 24 dla celów porównania
    if (eventEndHour === 0 && eventStartHour > 0) {
      eventEndHour = 24;
    }

    // Jeśli endMinute > 0, włącz też ostatni kafelek (np. dla 14:15 włącz kafelek 14:00)
    if (eventEndMinute > 0) {
      return hour >= eventStartHour && hour <= eventEndHour;
    }

    return hour >= eventStartHour && hour < eventEndHour;
  });
};

/**
 * Sprawdza czy dana data to dzisiaj
 */
export const isTodayDate = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Formatuje czas w formacie HH:mm
 */
export const formatTime = (time: LocalTime | string | null | undefined): string => {
  if (!time) return '';

  if (typeof time === 'object' && 'hour' in time) {
    const hours = String(time.hour).padStart(2, '0');
    const minutes = String(time.minute).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  if (typeof time === 'string') {
    // Jeśli to string "HH:mm:ss", zwróć pierwsze 5 znaków
    return time.substring(0, 5);
  }

  return '';
};

/**
 * Sprawdza czy dzień jest weekendem (sobota=5, niedziela=6 w naszym systemie 0=poniedziałek)
 */
export const isWeekend = (dayIndex: number): boolean => {
  return dayIndex === 5 || dayIndex === 6;
};
